/* eslint-disable import/no-unresolved */

import { h } from 'vue';
import { createVueApp } from '@vuejs/entry';
import { sharedPinia } from '@vuejs/pinia';

const readLandingData = () => {
    const el = document.getElementById('doda-data');

    if (!el) {
        return {};
    }

    try {
        return JSON.parse(el.textContent);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[doda] Niepoprawny JSON w #doda-data', err);

        return {};
    }
};

async function mountProductSliders(data) {
    const mounts = document.querySelectorAll('[data-doda-look]');

    if (!mounts.length) {
        return;
    }

    const looks = data.looks ?? [];
    const { default: ProductsSlider } = await import(
        /* webpackChunkName: "ProductsSlider" */ '@vuejs/components/recommendations/ProductsSlider.vue'
    );

    mounts.forEach((mount) => {
        const productIds = looks[Number(mount.dataset.dodaLook)]?.products ?? [];

        if (!productIds.length) {
            return;
        }

        // createVueApp nie przyjmuje propsów roota, więc owijamy komponent renderem.
        const app = createVueApp({
            render: () => h(ProductsSlider, { productIds }),
        });

        // AsyncProductItem w środku sięga po useAuthStore.
        app.use(sharedPinia);

        app.mount(mount);
    });
}

async function mountDropForm() {
    const mount = document.querySelector('#doda-drop-app');

    if (!mount) {
        return;
    }

    const { default: DodaDropApp } = await import(
        /* webpackChunkName: "DodaDropApp" */ '@vuejs/apps/DodaDropApp.vue'
    );

    createVueApp(DodaDropApp).mount(mount);
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/*
Parallaxa mediow: kazde .doda-px__media siedzi w kadrze .doda-px z overflow: hidden
i jest od niego wyzsze o zapas doklejony w CSS (--doda-px-slack). Przesuwamy je
translate'em wylacznie w granicach tego zapasu, wiec kadr nigdy nie odslania tla.
*/
function initParallax(root) {
    if (
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        !('IntersectionObserver' in window)
    ) {
        return;
    }

    const items = Array.from(root.querySelectorAll('.doda-px__media'))
        .filter((el) => el.parentElement)
        .map((el) => ({ el, frame: el.parentElement, slack: 0, shift: null }));

    if (!items.length) {
        return;
    }

    // Dopiero ta klasa wypycha media z flow i dokleja zapas.
    root.classList.add('js-doda-parallax');

    const byFrame = new Map(items.map((item) => [item.frame, item]));
    const active = new Set();
    let raf = 0;

    const measure = () => {
        items.forEach((item) => {
            item.slack = Math.max(0, (item.el.offsetHeight - item.frame.clientHeight) / 2);
        });
    };

    const paint = () => {
        raf = 0;

        const viewport = window.innerHeight;

        active.forEach((item) => {
            // Kadr schowany (np. detal historii ponizej 900px) nie ma zapasu.
            if (!item.slack) {
                return;
            }

            const rect = item.frame.getBoundingClientRect();
            // -1 = kadr tuz pod ekranem, 0 = na srodku, 1 = tuz nad ekranem.
            const progress = clamp(
                (viewport / 2 - (rect.top + rect.height / 2)) / ((viewport + rect.height) / 2),
                -1,
                1
            );
            const shift = Math.round(progress * item.slack * 10) / 10;

            if (shift !== item.shift) {
                item.shift = shift;
                item.el.style.transform = `translate3d(0, ${shift}px, 0)`;
            }
        });
    };

    const request = () => {
        if (!raf) {
            raf = window.requestAnimationFrame(paint);
        }
    };

    // Liczymy tylko kadry w okolicy ekranu - reszta czeka bezczynnie.
    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                const item = byFrame.get(entry.target);

                if (!item) {
                    return;
                }

                if (entry.isIntersecting) {
                    active.add(item);
                } else {
                    active.delete(item);
                }
            });

            request();
        },
        { rootMargin: '20% 0px' }
    );

    items.forEach((item) => observer.observe(item.frame));

    measure();
    request();

    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', () => {
        measure();
        request();
    });
}

/*
Chowanie headera przy scrollu w dol (desktop). Header (.main-header) jest position:
sticky top:0 wspolny dla calego sklepu - nie ruszamy go globalnie, tylko doklejamy
klase .is-doda-hidden, ktora na tym landingu przesuwa go translate'em poza ekran.
Scroll w gore klase zdejmuje. Na mobile (poizej 992px) nic nie robimy.
*/
function initHeaderHide() {
    const header = document.querySelector('.main-header');

    if (!header) {
        return;
    }

    const desktop = window.matchMedia('(min-width: 992px)');
    // Nizej tego progu zostawiamy header widoczny - na samej gorze strony ma byc.
    const revealZone = 200;
    // Ignorujemy mikro-drgania scrolla, zeby header nie migotal.
    const threshold = 6;

    let lastY = window.scrollY;
    let raf = 0;

    const update = () => {
        raf = 0;

        const y = window.scrollY;
        const delta = y - lastY;

        if (Math.abs(delta) < threshold) {
            return;
        }

        if (!desktop.matches || y <= revealZone) {
            header.classList.remove('is-doda-hidden');
        } else if (delta > 0) {
            header.classList.add('is-doda-hidden');
        } else {
            header.classList.remove('is-doda-hidden');
        }

        lastY = y;
    };

    const request = () => {
        if (!raf) {
            raf = window.requestAnimationFrame(update);
        }
    };

    window.addEventListener('scroll', request, { passive: true });

    // Po przejsciu na mobile chowanie nie ma sensu - przywracamy header.
    desktop.addEventListener('change', () => {
        if (!desktop.matches) {
            header.classList.remove('is-doda-hidden');
        }
    });
}

function initReveal(root) {
    if (
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        !('IntersectionObserver' in window)
    ) {
        return;
    }

    root.classList.add('js-doda-reveal');

    // Schodkowe sekcje: kazde dziecko dostaje swoj indeks, z ktorego CSS liczy delay.
    root.querySelectorAll('.doda-reveal--steps').forEach((container) => {
        Array.from(container.children).forEach((child, index) => {
            child.style.setProperty('--doda-step', String(index));
        });
    });

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-in');
                    observer.unobserve(entry.target);
                }
            });
        },
        { threshold: 0.12 }
    );

    root.querySelectorAll('.doda-reveal').forEach((el) => observer.observe(el));
}

document.addEventListener('DOMContentLoaded', () => {
    const root = document.querySelector('.savicki-doda');

    if (!root) {
        return;
    }

    const data = readLandingData();

    initReveal(root);
    initParallax(root);
    initHeaderHide();
    mountProductSliders(data);
    mountDropForm();
});
